import React from 'react';
import { Box, Chip, List, ListItemButton, Typography, useTheme } from '@mui/material';
import { ARIA_QUEUE_NAV_ITEMS } from '../ariaHandsoffConstants';
import {
  ariaMutedPanelSx,
  ariaQueueCountChipSx,
  ariaQueueRailItemSx,
  ariaQueueStripButtonSx,
} from '../ariaHandsoffSurfaces';

const QueueCountChip = ({ count, selected }) => {
  const theme = useTheme();

  return (
    <Chip
      label={count}
      size="small"
      sx={{
        height: 20,
        minWidth: 20,
        flexShrink: 0,
        fontSize: '0.625rem',
        fontWeight: 700,
        ...ariaQueueCountChipSx(selected, theme),
        '& .MuiChip-label': { px: 0.625 },
      }}
    />
  );
};

/** Vertical rail for md+; horizontal scroll strip for xs/sm. */
const AriaQueueNav = ({
  activeQueue,
  queueCounts = {},
  allCount = 0,
  onSelectQueue,
  variant = 'rail',
  navItems = ARIA_QUEUE_NAV_ITEMS,
}) => {
  const theme = useTheme();

  const items = navItems.map((queue) => ({
    ...queue,
    selected: activeQueue === queue.value,
    count: queue.value === 'all' ? allCount : queueCounts[queue.value] || 0,
  }));

  if (variant === 'strip') {
    return (
      <Box
        sx={{
          flexShrink: 0,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          ...ariaMutedPanelSx,
          px: 1.25,
          py: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'text.disabled',
            mb: 0.75,
            px: 0.5,
          }}
        >
          Escalation queues
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 0.75,
            overflowX: 'auto',
            pb: 0.25,
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'action.disabled', borderRadius: 4 },
          }}
        >
          {items.map((queue) => (
            <Box
              key={queue.value}
              component="button"
              type="button"
              onClick={() => onSelectQueue?.(queue.value)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                flexShrink: 0,
                borderRadius: '999px',
                px: 1.25,
                py: 0.75,
                cursor: 'pointer',
                font: 'inherit',
                fontWeight: queue.selected ? 700 : 600,
                fontSize: '0.6875rem',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                ...ariaQueueStripButtonSx(queue.selected, theme),
              }}
            >
              {queue.label}
              <QueueCountChip count={queue.count} selected={queue.selected} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        width: { md: 180, lg: 200 },
        flexShrink: 0,
        borderRight: (theme) => `1px solid ${theme.palette.divider}`,
        ...ariaMutedPanelSx,
      }}
    >
      <Box sx={{ px: 1.75, pt: 2, pb: 1, flexShrink: 0 }}>
        <Typography
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'text.disabled',
          }}
        >
          Escalation queues
        </Typography>
      </Box>

      <List
        disablePadding
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: 1,
          pb: 1.5,
        }}
      >
        {items.map((queue) => (
          <ListItemButton
            key={queue.value}
            selected={queue.selected}
            onClick={() => onSelectQueue?.(queue.value)}
            sx={{
              borderRadius: '10px',
              mb: 0.5,
              py: 1,
              px: 1.25,
              alignItems: 'center',
              gap: 1,
              ...ariaQueueRailItemSx(queue.selected, theme),
            }}
          >
            <Typography
              sx={{
                flex: 1,
                minWidth: 0,
                fontWeight: queue.selected ? 700 : 600,
                fontSize: { md: '0.6875rem', lg: '0.75rem' },
                color: queue.selected ? 'primary.main' : 'text.primary',
                lineHeight: 1.3,
              }}
            >
              {queue.label}
            </Typography>
            <QueueCountChip count={queue.count} selected={queue.selected} />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
};

export default AriaQueueNav;
